import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Button from '../../../../components/ui/Button';
import FormInput from '../../../../components/ui/FormInput';
import DeliveryMethodSelection from '../shared/DeliveryMethodSelection';
import { formatCurrency } from '../../../../utils/formatters';
import { setUseEstimator } from '../../../../redux/slices/deliverySlice';

const BuyActions = ({ product, onAddToCart, shippingRate, onAddressSelect }) => {
  const { name, price, stock, productType } = product;
  const isVirtual = productType === 'virtual';
  
  // Get shipping state from Redux for access to useEstimator flag
  const shippingState = useSelector(state => state.shipping);
  const dispatch = useDispatch();
  
  const [quantity, setQuantity] = useState(1);
  // No default delivery option selected
  const [deliveryOption, setDeliveryOption] = useState(isVirtual ? 'virtual' : null);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [showDeliveryPrompt, setShowDeliveryPrompt] = useState(false);

  // Initialize delivery option if not already set - MUST BE BEFORE CONDITIONAL RETURN
  useEffect(() => {
    if (!product) return;
    
    // For virtual products, set delivery option to 'virtual'
    const isVirtual = product?.productType === 'virtual';
    if (isVirtual && deliveryOption !== 'virtual') {
      setDeliveryOption('virtual');
    }
  }, [product, deliveryOption]);

  // When component loads, set useEstimator to true for shipping option
  useEffect(() => {
    if (dispatch && deliveryOption === 'shipping' && !shippingState?.useEstimator) {
      dispatch(setUseEstimator(true));
    }
  }, [dispatch, shippingState, deliveryOption]);

  // Enable Add to Cart when using shipping estimates
  useEffect(() => {
    if (deliveryOption === 'shipping' && !shippingRate && shippingState?.useEstimator) {
      console.log('Using shipping estimate for cart actions');
    }
  }, [deliveryOption, shippingRate, shippingState]);

  // Always use estimate when shipping is selected
  useEffect(() => {
    if (deliveryOption === 'shipping' && !shippingRate) {
      // Create and set an estimated shipping rate
      const estimatedRate = {
        provider: 'Estimated',
        serviceName: 'Standard',
        amount: '5.99',
        currency: 'USD',
        days: '1-3',
        isEstimate: true
      };
      
      // If the component has access to shipping rate selection function
      if (onAddressSelect && typeof onAddressSelect === 'function') {
        // This will trigger shipping rate calculation/selection
        onAddressSelect({
          id: 'estimate',
          forceEstimate: true
        });
      }
    }
  }, [deliveryOption, shippingRate, onAddressSelect]);

  if (!product || !product.forSell) return null;

  const { 
    currency = 'USD',
    openToPickUp,
    openToShipping,
    pickUpAddress
  } = product;

  // Get shipping cost from the selected rate if available
  const shippingCost = shippingRate ? parseFloat(shippingRate.amount) : 0;

  const calculateTotal = () => {
    const itemTotal = quantity * price;
    return deliveryOption === 'shipping' && shippingRate ? itemTotal + shippingCost : itemTotal;
  };

  const total = calculateTotal();

  // Show delivery prompt and hide after a few seconds
  const promptDeliverySelection = () => {
    setShowDeliveryPrompt(true);
    
    // Hide prompt after a few seconds
    setTimeout(() => {
      setShowDeliveryPrompt(false);
    }, 3000);
  };

  const handleCartAction = (navigateToCart) => {
    // Check if delivery option is selected for non-virtual products
    if (!isVirtual && !deliveryOption) {
      promptDeliverySelection();
      return;
    }

    // Check if out of stock
    if (!isVirtual && stock === '0') {
      return; // Don't proceed if out of stock
    }

    // Check if shipping is selected but no rate is available
    if (deliveryOption === 'shipping' && !shippingRate && !shippingState?.useEstimator) {
      return; // Don't proceed if shipping selected but no rate
    }

    // Create a fallback shipping rate when using estimates
    const fallbackRate = !shippingRate && deliveryOption === 'shipping' && shippingState?.useEstimator
      ? { amount: shippingCost, currency, isEstimate: true, provider: 'Estimated', serviceName: 'Standard' }
      : shippingRate;
      
    onAddToCart?.({
      type: 'buy',
      quantity: isVirtual ? 1 : quantity,
      deliveryMethod: deliveryOption,
      shippingRate: fallbackRate,
      navigateToCart
    });
  };

  return (
    <div className="space-y-6 max-w-md">
      {/* Buy Form */}
      <div className="space-y-4">
       

        {/* For virtual products, display info message */}
        {isVirtual && (
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-blue-700">
              This is a virtual fashion item. After purchase, it will be available in your digital closet.
            </p>
          </div>
        )}

        {/* Delivery Options - Only for physical products */}
        {!isVirtual && (
          <div className="mt-4">
            <div className="flex justify-between items-center">
              <label className="block text-sm font-medium text-gray-700">
                Delivery Method
              </label>
              {showDeliveryPrompt && (
                <div className="text-orange-500 text-sm font-medium animate-pulse">
                  ← Please select a delivery method
                </div>
              )}
            </div>
            <DeliveryMethodSelection
              openToShipping={openToShipping}
              openToPickUp={openToPickUp}
              deliveryOption={deliveryOption}
              setDeliveryOption={setDeliveryOption}
              pickUpAddress={pickUpAddress}
              shippingCost={shippingCost}
              currency={shippingRate?.currency || currency}
              shippingRate={shippingRate}
              isShippingRateSelected={!!shippingRate}
              onAddressSelect={onAddressSelect}
            />
          </div>
        )}

        {/* Out of stock message - not relevant for virtual products */}
        {!isVirtual && stock === '0' && (
          <div className="bg-red-50 p-4 rounded-lg">
            <p className="text-sm text-red-600">
              This item is currently out of stock.
            </p>
          </div>
        )}

        {/* Buy Form Actions */}
        <div className="flex flex-row gap-4 mt-6">
          <Button
            variant="orange"
            className="w-full"
            onClick={() => handleCartAction(true)}
          >
            Buy Now
          </Button>
          <Button
            variant="teal"
            className="w-full"
            onClick={() => handleCartAction(false)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="8" cy="21" r="1" />
              <circle cx="19" cy="21" r="1" />
              <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
              <line x1="12" y1="9" x2="12" y2="15" />
              <line x1="9" y1="12" x2="15" y2="12" />
            </svg>
          </Button>
          <Button
            variant="offer"
            className="w-full"
          >
            Offer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BuyActions; 